import { Check, Loader2, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { toast } from "sonner";

import type { AssistantAudienceProfileCreationPreviewReference } from "@/lib/assistant-audience-profile-creation-previews";
import { trpc } from "@/lib/trpc";
import { Button } from "@runcheetah/ui";

type CreationState =
  | { status: "active" }
  | { status: "cancelled" }
  | { status: "created"; profileId: string; activated: boolean };

export function AssistantAudienceProfileCreationPreviews({
  brandId,
  sourceChatId,
  references,
  isStale = false,
}: {
  brandId: string;
  sourceChatId?: string | null;
  references: AssistantAudienceProfileCreationPreviewReference[];
  isStale?: boolean;
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {references.map((reference) => (
        <AssistantAudienceProfileCreationPreviewCard
          key={reference.id}
          brandId={brandId}
          sourceChatId={sourceChatId}
          reference={reference}
          isStale={isStale}
        />
      ))}
    </div>
  );
}

function AssistantAudienceProfileCreationPreviewCard({
  brandId,
  sourceChatId,
  reference,
  isStale,
}: {
  brandId: string;
  sourceChatId?: string | null;
  reference: AssistantAudienceProfileCreationPreviewReference;
  isStale: boolean;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const storageKey = getCreationStorageKey(brandId, reference.id);
  const [creationState, setCreationState] = useState<CreationState>(() =>
    loadCreationState(storageKey),
  );
  const [activate, setActivate] = useState(reference.activateByDefault);
  const fields = buildAudienceProfileFields(reference, intl);
  const createProfile = trpc.audienceProfile.create.useMutation({
    onSuccess: async (profile) => {
      const nextState = {
        status: "created" as const,
        profileId: profile.id,
        activated: profile.isActive,
      };
      saveCreationState(storageKey, nextState);
      setCreationState(nextState);
      await utils.audienceProfile.list.invalidate({ brandId });
      toast.success(
        intl.formatMessage({
          id: "assistantAudienceProfileCreationPreview.createSuccess",
          defaultMessage: "Audience profile created",
        }),
      );
    },
  });

  const cancel = () => {
    const nextState = { status: "cancelled" as const };
    saveCreationState(storageKey, nextState);
    setCreationState(nextState);
  };

  const confirm = () => {
    if (isStale || creationState.status !== "active" || createProfile.isPending) {
      return;
    }

    createProfile.mutate(
      {
        brandId,
        name: reference.name,
        description: reference.description,
        profile: reference.profile,
        activate,
        source: "chat",
        sourceBrandAssistantChatId: sourceChatId ?? null,
      },
      {
        onError(error) {
          toast.error(error.message);
        },
      },
    );
  };

  if (creationState.status === "cancelled") {
    return (
      <div className="rounded-3xl border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
        <FormattedMessage
          id="assistantAudienceProfileCreationPreview.cancelled"
          defaultMessage="Audience profile proposal cancelled."
        />
      </div>
    );
  }

  if (creationState.status === "created") {
    return (
      <article className="rounded-3xl border border-border/70 bg-muted/30 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
          <Check className="h-4 w-4 text-primary" aria-hidden="true" />
          <FormattedMessage
            id="assistantAudienceProfileCreationPreview.created"
            defaultMessage="Audience profile created"
          />
          {creationState.activated ? (
            <span className="text-muted-foreground">
              <FormattedMessage
                id="assistantAudienceProfileCreationPreview.createdActive"
                defaultMessage="and set active"
              />
            </span>
          ) : null}
        </div>
      </article>
    );
  }

  return (
    <article className="border-y border-border/60 bg-transparent">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/88">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <FormattedMessage
                id="assistantAudienceProfileCreationPreview.title"
                defaultMessage="Create this audience profile?"
              />
            </div>
            <div className="max-w-2xl space-y-1 pl-9">
              <p className="text-sm font-semibold leading-6 text-foreground">{reference.name}</p>
              {reference.summary || reference.description ? (
                <p className="text-sm leading-6 text-muted-foreground">
                  {reference.summary ?? reference.description}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {fields.length > 0 ? (
          <dl className="grid gap-3 rounded-2xl border border-border/60 bg-card/45 p-3 sm:grid-cols-2">
            {fields.map((field) => (
              <div key={field.label} className="space-y-1">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {field.label}
                </dt>
                <dd className="text-sm leading-5 text-foreground/82">{field.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {isStale ? (
          <div className="border-t border-border/60 pt-3 text-sm text-muted-foreground">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <X className="text-muted-foreground/40" size="16" aria-hidden="true" />
              <span className="font-medium text-foreground/58">
                <FormattedMessage
                  id="assistantAudienceProfileCreationPreview.superseded"
                  defaultMessage="This proposal can no longer be accepted because the conversation has moved forward."
                />
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-3">
            <label className="flex min-w-0 items-center gap-2 text-sm font-medium text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border text-primary accent-primary"
                checked={activate}
                disabled={createProfile.isPending}
                onChange={(event) => setActivate(event.target.checked)}
              />
              <FormattedMessage
                id="assistantAudienceProfileCreationPreview.activate"
                defaultMessage="Make active for new generation"
              />
            </label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={createProfile.isPending}
                onClick={cancel}
              >
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="button" size="sm" disabled={createProfile.isPending} onClick={confirm}>
                {createProfile.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                <FormattedMessage
                  id="assistantAudienceProfileCreationPreview.create"
                  defaultMessage="Create profile"
                />
              </Button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function buildAudienceProfileFields(
  reference: AssistantAudienceProfileCreationPreviewReference,
  intl: IntlShape,
) {
  const targetAudience = reference.profile.targetAudience;
  const fields = [
    {
      label: intl.formatMessage({
        id: "audienceProfiles.form.primarySegments",
        defaultMessage: "Primary segments",
      }),
      value: formatValue(targetAudience.primaryAudienceSegments),
    },
    {
      label: intl.formatMessage({
        id: "audienceProfiles.form.secondarySegments",
        defaultMessage: "Secondary segments",
      }),
      value: formatValue(targetAudience.secondaryAudienceSegments),
    },
    {
      label: intl.formatMessage({
        id: "audienceProfiles.form.painPoints",
        defaultMessage: "Pain points",
      }),
      value: formatValue(targetAudience.painPoints),
    },
    {
      label: intl.formatMessage({
        id: "audienceProfiles.form.buyingTriggers",
        defaultMessage: "Buying triggers",
      }),
      value: formatValue(targetAudience.buyingTriggers),
    },
    {
      label: intl.formatMessage({
        id: "audienceProfiles.form.decisionDrivers",
        defaultMessage: "Decision drivers",
      }),
      value: formatValue(targetAudience.decisionMakingDrivers),
    },
    {
      label: intl.formatMessage({
        id: "audienceProfiles.form.demographics",
        defaultMessage: "Demographics",
      }),
      value: formatValue(targetAudience.demographics),
    },
  ];

  return fields.filter((field) => field.value);
}

function formatValue(value: string | readonly string[] | undefined): string | null {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : null;
  }

  if (typeof value === "string") {
    const normalized = value.replace(/\s+/g, " ").trim();
    return normalized || null;
  }

  return null;
}

function getCreationStorageKey(brandId: string, proposalId: string) {
  return `assistant-audience-profile-creation:${brandId}:${proposalId}`;
}

function loadCreationState(key: string): CreationState {
  if (typeof window === "undefined") {
    return { status: "active" };
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return { status: "active" };
    }

    const parsed = JSON.parse(raw) as Partial<CreationState>;
    if (parsed.status === "cancelled") {
      return { status: "cancelled" };
    }
    if (
      parsed.status === "created" &&
      typeof parsed.profileId === "string" &&
      typeof parsed.activated === "boolean"
    ) {
      return {
        status: "created",
        profileId: parsed.profileId,
        activated: parsed.activated,
      };
    }
  } catch {
    // Losing local proposal state should not block profile creation.
  }

  return { status: "active" };
}

function saveCreationState(key: string, state: CreationState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Losing local proposal state should not block profile creation.
  }
}
